import ReactGA from "react-ga4";
import i18n from "i18n";
import { EVENTS } from "utils/analytics.events";
import { GOOGLE_TAG_EXEMPT_RULES, isTokenlessLinkLanding, REDACTED, sanitizeRoute, sanitizeUrl, sanitizeUrlsInText } from "utils/privacy/telemetryUrl";

const GA_MEASUREMENT_ID = "G-EQSZKSS09D";

const isDev = process.env.NODE_ENV !== "production";

// GA4, Google Ads and the Meta pixel are PRODUCTION-ONLY (AGENTS.md → External services).
// `NODE_ENV` cannot express that: `npm run build` sets it to "production" for the dev, stage
// and preprod Cloud Run builds too, while gtag.js, the GTM container and fbq are loaded
// unconditionally in `public/index.html` on every host. So every internal test purchase on
// localhost / dev / stage / preprod fired a REAL Google Ads conversion and a REAL Meta
// Purchase, and non-localhost builds also sent a real GA4 `purchase_plan_*` hit. That is one
// confirmed contamination path behind Aug 12 – Sep 11's 83 `ads_conversion_purchase` and 45 GA4
// purchases against 22 paid subscriptions (and 5 conversions on 2026-09-17, a day with no sale)
// — not proven to be the only one: free trials and GTM-side tags are others (REF-6600).
// Same host test Hotjar (`public/index.html`) and Sentry (`src/instrument.ts`) already use.
const PRODUCTION_APP_HOST = "app.refabric.com";

export const isProductionApp = () => typeof window !== "undefined" && window.location.hostname === PRODUCTION_APP_HOST;

let isInitialized = false;

// ── URL privacy (REF-6708 privacy B2) ─────────────────────────────────────────────────────────────
// GA reads the page URL itself (`page_location` / `page_referrer` default to the raw document URL and
// referrer), and a pathname alone already carries reset / invite / magic-link tokens. Every OWNED hit
// therefore carries explicit, sanitised page fields — explicit event params outrank config and `set` in
// gtag's precedence — built by the shared policy in utils/privacy/telemetryUrl (the same one Sentry
// uses). Only URL VALUES change: hit names, counts and every other param are sent as before.

/**
 * Event params whose value IS a URL or a route: sanitised with `sanitizeUrl`, which fails closed (an opaque
 * `data:` / `blob:` / `javascript:` value becomes `[redacted]`). Every OTHER string param still gets the text
 * pass (`sanitizeUrlsInText`), which rewrites only URL- and path-shaped substrings carrying a secret, so a
 * URL under an unlisted key (`Size_Error.result` was one) cannot reach GA raw; labels, enums and counts are
 * unchanged. Non-string params are sent untouched.
 */
export const GA_URL_PARAM_KEYS = ["route", "from_route", "to_route", "page_route", "request_path", "result_url", "image_url", "url", "page", "page_path"] as const;

const sanitizedLocation = () => sanitizeUrl(window.location.href);

/**
 * The page the user is on, keyed by the RAW pathname — the same key page_view counting uses, and never the
 * sanitised path (two different tokens on one route sanitise alike but are two pages). Resolved from the
 * live location on EVERY hit, not only when a page_view is sent: a child's mount event fires before the
 * parent's tracking effect, and must already carry the new page's referrer.
 */
let currentPage: { path: string; referrer: string; lastLocation: string } | null = null;

const resolvePage = () => {
  const location = sanitizedLocation();
  const path = window.location.pathname;
  if (!currentPage || currentPage.path !== path) {
    // A new page: its referrer is the previous page's last sanitised location (first page: document.referrer).
    currentPage = { path, referrer: currentPage ? currentPage.lastLocation : sanitizeUrl(document.referrer), lastLocation: location };
  } else {
    currentPage.lastLocation = location;
  }

  return { location, referrer: currentPage.referrer };
};

/** Explicit page fields for an event or conversion; spread LAST so a caller's param cannot override them. */
const pageFields = () => {
  const { location, referrer } = resolvePage();

  return { page_location: location, page_referrer: referrer };
};

const sanitizeUrlParams = (params?: Record<string, any>): Record<string, any> => {
  const out: Record<string, any> = { ...params };
  Object.keys(out).forEach((key) => {
    if (typeof out[key] !== "string") return;
    out[key] = (GA_URL_PARAM_KEYS as readonly string[]).includes(key) ? sanitizeUrl(out[key]) : sanitizeUrlsInText(out[key]);
  });

  return out;
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

/**
 * REF-6858 (P1-a′): may the Google tag load at this URL? Yes on an ordinary page (the policy leaves the PATH alone)
 * and on a purchase route (`GOOGLE_TAG_EXEMPT_RULES`); no on every other secret route (/reset-password/<token> …), nor
 * on a tokenless link page whose query the policy changes (`/reset-password?email=…`, D2).
 * The same decision as the head bootstrap's `__rfGoogleTagAllowed`. Fails closed (no) on anything unusable.
 */
export const googleTagAllowedAt = (href: string): boolean => {
  try {
    const raw = new URL(href);
    const clean = new URL(sanitizeUrl(raw.href));
    if (!/^https?:$/.test(clean.protocol) || clean.origin !== raw.origin) return false;
    if (clean.pathname === raw.pathname) return !isTokenlessLinkLanding(raw, clean);
    const parts = clean.pathname.split("/").filter((part) => part !== "");

    return GOOGLE_TAG_EXEMPT_RULES.some(
      (rule) =>
        rule.length === parts.length && rule.every((segment, i) => (typeof segment.literal === "string" ? parts[i].toLowerCase() === segment.literal : !segment.secret || parts[i] === REDACTED))
    );
  } catch {
    return false;
  }
};

export const initializeAnalytics = () => {
  if (isInitialized) return;
  // REF-6858 (P1-a′): react-ga4 loads the same Google tag as the head, whose linked Ads destinations read the raw
  // window URL. On a secret-token route it is NOT loaded yet: commands sent meanwhile wait in `window.dataLayer`
  // (sanitised, as always) and `trackPageView` initialises on the first route where the tag is allowed.
  if (!googleTagAllowedAt(window.location.href)) return;

  // testMode swallows the hit locally AND on the non-production Cloud Run builds, which
  // `isDev` alone did not (their NODE_ENV is "production"). See isProductionApp above.
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    testMode: !isProductionApp(),
    gtagOptions: { page_location: sanitizedLocation(), page_referrer: sanitizeUrl(document.referrer) },
  });

  isInitialized = true;

  if (isDev) {
    console.log("[Analytics] Initialized in test mode");
  }
};

export const setAnalyticsUser = (userId: string, language?: string) => {
  const lang = language ?? i18n.language;

  ReactGA.set({ userId, language: lang });

  if (isDev) {
    console.log("[Analytics] setUser:", userId, lang);
  }
};

export const clearAnalyticsUser = () => {
  ReactGA.set({ userId: undefined });

  if (isDev) {
    console.log("[Analytics] clearUser");
  }
};

export const trackPageView = (path: string) => {
  initializeAnalytics();
  const { location, referrer } = resolvePage();
  const page = sanitizeRoute(path);

  // Also for hits gtag builds itself (vendor-internal); the explicit page_view fields below are the guarantee.
  ReactGA.set({ page_location: location, page_referrer: referrer });
  ReactGA.send({
    hitType: "pageview",
    page,
    location,
    page_referrer: referrer,
    title: document.title,
    language: i18n.language,
  });

  if (isDev) {
    console.log("[Analytics] pageview:", page, i18n.language);
  }
};

export const trackEvent = (name: string, params?: Record<string, any>) => {
  if (isDev) {
    console.log("[Analytics] Event:", name, params || {});
  }

  ReactGA.event(name, {
    ...sanitizeUrlParams(params),
    ...pageFields(),
  });
};

export type SignUpMethod = "google" | "email" | "sso" | "invite";

/**
 * Fire the canonical `sign_up` event on a completed signup, regardless of method.
 *
 * Every signup-completion path must call this so GA4 can count total signups from
 * one event (with a `method` breakdown) instead of three fragmented per-method
 * events. This closes the team-invite gap (invites previously fired no signup
 * event at all). Per-method events (google_sign_up / signup_email_signup_btn /
 * signup_sso) are still fired alongside for existing dashboards.
 *
 * Call only when a NEW user is actually created — not on login of an existing user.
 */
export const trackSignUpCompleted = (method: SignUpMethod) => {
  trackEvent(EVENTS.AUTH.SIGNUP, { method });
};

export type DownloadFormat = "png" | "jpg" | "webp" | "svg" | "mp4" | "pdf" | "xlsx";

/**
 * Fire a download event with the canonical attribute set, so GA4 can break downloads down by
 * format / resolution / surface once those params are registered as custom dimensions (forward-only).
 *
 * Additive — pass whichever attrs apply (`resolution` is raster-only; `is_zip`/`count` for bulk).
 * Use on every real download (not on "open menu" clicks). Full plan + call-site list:
 * docs/handover/ga4-download-attributes.md.
 */
export const trackDownload = (eventName: string, attrs: { format?: DownloadFormat | string; resolution?: "2k" | "4k"; surface?: string; is_zip?: boolean; count?: number } = {}) =>
  trackEvent(eventName, attrs);

export const trackConversion = (conversionId: string, params?: Record<string, any>) => {
  if (isProductionApp() && typeof window.gtag !== "undefined") {
    window.gtag("event", "conversion", {
      send_to: conversionId,
      ...sanitizeUrlParams(params),
      ...pageFields(),
    });
  }

  if (isDev) {
    console.log("[Analytics] Conversion:", conversionId, params || {});
  }
};

export const trackFacebookEvent = (eventName: string, params?: Record<string, any>) => {
  if (isProductionApp() && typeof window.fbq !== "undefined") {
    window.fbq("track", eventName, params);
  }

  if (isDev) {
    console.log("[Analytics] Facebook:", eventName, params || {});
  }
};
